import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import {
    fetchDeviceSchedules, fetchSwitchableDevices, createDeviceSchedule, updateDeviceSchedule, deleteDeviceSchedule,
    fetchDeviceScheduleRuns, runDeviceScheduleNow, recheckDeviceScheduleRun,
    DeviceSchedule, DeviceScheduleInput, DeviceScheduleRun, RunStatus, ScheduleAction, SwitchableDevice,
} from '../services/deviceSchedulesApi';
import { fetchContactGroups, ContactGroup } from '../services/alertsApi';
import DeviceScheduleEditor, { dayKey } from './deviceSchedules/DeviceScheduleEditor';
import PageHeader from './shared/PageHeader';
import { ui } from '../styles/shared';
import { fmtDateTime } from '../utils/format';
import { confirmAction } from '../utils/confirm';

/**
 * Schemalagd styrning: slå på eller av en grupp enheter vid bestämda tillfällen,
 * t.ex. alla kylar inför ett arbetspass, och se att det blev gjort.
 */

const STATUS: Record<RunStatus, { text: string; bg: string; color?: string }> = {
    planned:   { text: 'Planerad',     bg: '#e8f0fe', color: '#1a4aa6' },
    running:   { text: 'Pågår',        bg: '#fff3cd', color: '#664d03' },
    ok:        { text: 'Klart',        bg: '#28a745' },
    failed:    { text: 'Misslyckades', bg: '#dc3545' },
    missed:    { text: 'Missades',     bg: '#dc3545' },
    cancelled: { text: 'Inställd',     bg: '#eee', color: '#555' },
};
const actionText = (a: ScheduleAction) => (a === 'ON' ? 'Slå på' : 'Slå av');
const Badge: React.FC<{ status: RunStatus }> = ({ status }) => {
    const s = STATUS[status];
    return <span style={ui.badge(s.bg, s.color)}>{s.text}</span>;
};

/** Körda tillfällen för ett schema, med utfall per enhet. */
const RunHistory: React.FC<{ scheduleId: number; canEdit: boolean; refreshKey: number; onError: (m: string) => void }> = ({ scheduleId, canEdit, refreshKey, onError }) => {
    const [runs, setRuns] = useState<DeviceScheduleRun[] | null>(null);
    const [busy, setBusy] = useState<number | null>(null);
    const load = useCallback(() => fetchDeviceScheduleRuns(scheduleId).then(setRuns).catch((e: any) => onError(e.message)), [scheduleId, onError]);
    useEffect(() => { load(); }, [load, refreshKey]);

    const recheck = async (runId: number) => {
        setBusy(runId);
        try { await recheckDeviceScheduleRun(runId); await load(); } catch (e: any) { onError(e.message); } finally { setBusy(null); }
    };

    if (!runs) return <div style={{ fontSize: 13, color: '#888', padding: 8 }}>Hämtar…</div>;
    if (runs.length === 0) return <div style={{ fontSize: 13, color: '#888', padding: 8 }}>Inget har körts än.</div>;
    return (
        <div style={{ overflowX: 'auto', marginTop: 8 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', background: 'white' }}>
                <thead><tr><th style={ui.th}>Tillfälle</th><th style={ui.th}>Utfall</th><th style={ui.th}>Enheter</th><th style={ui.th}></th></tr></thead>
                <tbody>
                    {runs.map(r => (
                        <tr key={r.id}>
                            <td style={{ ...ui.td, whiteSpace: 'nowrap' }}>{fmtDateTime(r.run_at)}<div style={{ fontSize: 12, color: '#777' }}>{actionText(r.action)}</div></td>
                            <td style={ui.td}><Badge status={r.status} /><div style={{ fontSize: 12, color: '#555', marginTop: 4, maxWidth: 360 }}>{r.summary}</div></td>
                            <td style={ui.td}>
                                {(r.items ?? []).map(i => (
                                    <div key={`${i.device_id}:${i.io_endpoint}`} style={{ fontSize: 13 }}>
                                        <span style={{ color: i.result === 'ok' ? '#198754' : i.result === 'failed' ? '#b02a37' : '#888' }}>
                                            {i.result === 'ok' ? '✓' : i.result === 'failed' ? '✗' : '–'}
                                        </span>{' '}
                                        {i.device_name}
                                        {i.power_value != null && <span style={{ color: '#777' }}> · {i.power_value} W</span>}
                                        {i.message && <span style={{ color: '#777' }}> · {i.message}</span>}
                                    </div>
                                ))}
                            </td>
                            <td style={{ ...ui.td, textAlign: 'right' }}>
                                {canEdit && (r.status === 'failed' || r.status === 'missed') && (
                                    <button onClick={() => recheck(r.id)} disabled={busy === r.id} style={ui.btnLight}
                                        title="Läser av läget igen utan att skicka nya kommandon">
                                        {busy === r.id ? 'Kontrollerar…' : 'Kontrollera igen'}
                                    </button>
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

const DeviceSchedules: React.FC = () => {
    const { isAdmin, isSuperuser } = useAuth();
    const canEdit = isAdmin || isSuperuser;
    const [schedules, setSchedules] = useState<DeviceSchedule[]>([]);
    const [devices, setDevices] = useState<SwitchableDevice[]>([]);
    const [groups, setGroups] = useState<ContactGroup[]>([]);
    const [edit, setEdit] = useState<DeviceSchedule | 'new' | null>(null);
    const [openHistory, setOpenHistory] = useState<number | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [message, setMessage] = useState<string | null>(null);
    const [refreshKey, setRefreshKey] = useState(0);

    const load = useCallback(async () => {
        try { setSchedules(await fetchDeviceSchedules()); } catch (e: any) { setError(e.message); }
    }, []);
    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        if (!canEdit) return;
        fetchSwitchableDevices().then(setDevices).catch(() => {});
        fetchContactGroups().then(setGroups).catch(() => {});
    }, [canEdit]);

    // Medan något pågår: hämta om var femte sekund så utfallet syns när det är klart.
    const anyRunning = schedules.some(s => s.last_run?.status === 'running');
    useEffect(() => {
        if (!anyRunning) return;
        const t = setInterval(() => { load(); setRefreshKey(k => k + 1); }, 5000);
        return () => clearInterval(t);
    }, [anyRunning, load]);

    // Andra schemans planerade dagar (dag → schemanamn): kalenderns krock-prick.
    const otherDays = useMemo(() => {
        const editingId = edit && edit !== 'new' ? edit.id : null;
        const m = new Map<string, string[]>();
        for (const s of schedules) {
            if (s.id === editingId) continue;
            const seen = new Set<string>();
            for (const r of s.runs) {
                const d = new Date(r.run_at);
                if (isNaN(d.getTime())) continue;
                const k = dayKey(d);
                if (seen.has(k)) continue;
                seen.add(k);
                m.set(k, [...(m.get(k) ?? []), s.name]);
            }
        }
        return m;
    }, [schedules, edit]);

    const save = async (body: DeviceScheduleInput) => {
        if (edit && edit !== 'new') await updateDeviceSchedule(edit.id, body);
        else await createDeviceSchedule(body);
        setEdit(null);
        await load();
    };
    const remove = async (s: DeviceSchedule) => {
        if (!confirmAction(`Ta bort schemat "${s.name}"?\n\nPlanerade tillfällen ställs in och schemats larmregel stängs av. Det som redan körts finns kvar i historiken.`)) return;
        try { await deleteDeviceSchedule(s.id); await load(); } catch (e: any) { setError(e.message); }
    };
    const runNow = async (s: DeviceSchedule, action: ScheduleAction) => {
        if (!confirmAction(`${actionText(action)} alla ${s.devices.length} enheter i "${s.name}" nu?`)) return;
        try {
            await runDeviceScheduleNow(s.id, action);
            setMessage(`Körningen har startat. Utfallet visas här när den är klar${s.power_check && action === 'ON' ? ` (efter effektkontrollen, minst ${s.power_delay_seconds} s)` : ''}.`);
            setOpenHistory(s.id);
            await load();
        } catch (e: any) { setError(e.message); }
    };

    return (
        <div>
            <PageHeader icon="⏱️" title="Schemalagd styrning"
                description="Slå på eller av en grupp enheter vid bestämda tillfällen, till exempel alla kylar inför ett arbetspass. Deviot kontrollerar att det blev gjort och larmar om någon enhet inte startar." />
            {error && <div style={ui.errorBox}><span>{error}</span><button onClick={() => setError(null)} style={ui.btnLightNeutral}>Stäng</button></div>}
            {message && <div style={ui.successBox}><span>{message}</span><button onClick={() => setMessage(null)} style={ui.btnLightNeutral}>Stäng</button></div>}

            {edit && (
                <DeviceScheduleEditor schedule={edit === 'new' ? null : edit} devices={devices} groups={groups}
                    others={otherDays} onSave={save} onCancel={() => setEdit(null)} />
            )}
            {canEdit && !edit && (
                <div style={{ marginBottom: 14 }}>
                    <button onClick={() => setEdit('new')} style={ui.btnPrimary} disabled={groups.length === 0}>+ Nytt schema</button>
                    {groups.length === 0 && <span style={{ fontSize: 13, color: '#888', marginLeft: 10 }}>Skapa en kontaktgrupp under Kontakter först. Den larmas om någon enhet inte startar.</span>}
                </div>
            )}

            {schedules.length === 0 ? (
                <div style={{ ...ui.card, color: '#666' }}>Inga scheman ännu.</div>
            ) : schedules.map(s => (
                <div key={s.id} style={{ ...ui.card, opacity: s.active ? 1 : 0.6 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                        <div style={{ minWidth: 0 }}>
                            <div style={{ fontWeight: 700, fontSize: 16 }}>{s.name}{!s.active && <span style={{ fontWeight: 400, fontSize: 13, color: '#777' }}> (avstängt)</span>}</div>
                            <div style={{ fontSize: 13, color: '#555', marginTop: 4 }}>
                                {s.devices.length} {s.devices.length === 1 ? 'enhet' : 'enheter'}: {s.devices.map(d => d.io_endpoint ? `${d.name} (utgång ${d.io_endpoint})` : d.name).join(', ')}
                            </div>
                            <div style={{ fontSize: 13, color: '#555', marginTop: 2 }}>
                                Larmar: <b>{s.fail_group_name ?? 'ingen grupp'}</b>
                                {s.fail_group_members === 0 && <span style={{ color: '#b02a37' }}> (gruppen har inga kontakter)</span>}
                                {s.ok_group_name && <> · Besked när allt gått bra: {s.ok_group_name}</>}
                                {s.power_check && <> · Effekt över {s.power_min_watts} W efter {s.power_delay_seconds} s</>}
                            </div>
                            {s.rule_enabled === false && s.active && (
                                <div style={{ fontSize: 13, color: '#b02a37', marginTop: 2 }}>Schemats larmregel är avstängd under Bevakning, så inga larm går ut.</div>
                            )}
                            <div style={{ fontSize: 13, marginTop: 8 }}>
                                {s.runs.length === 0
                                    ? <span style={{ color: '#888' }}>Inga planerade tillfällen.</span>
                                    : <>Nästa: {s.runs.slice(0, 4).map(r => `${actionText(r.action).toLowerCase()} ${fmtDateTime(r.run_at)}`).join(' · ')}{s.runs.length > 4 && ` · +${s.runs.length - 4} till`}</>}
                            </div>
                            {s.last_run && (
                                <div style={{ fontSize: 13, marginTop: 6, display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
                                    Senast: <Badge status={s.last_run.status} />
                                    <span style={{ color: '#555' }}>{fmtDateTime(s.last_run.run_at)}, {actionText(s.last_run.action).toLowerCase()}. {s.last_run.summary}</span>
                                </div>
                            )}
                        </div>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                            {canEdit && <button onClick={() => runNow(s, 'ON')} style={ui.btnLight} disabled={!s.active}>Slå på nu</button>}
                            {canEdit && <button onClick={() => runNow(s, 'OFF')} style={ui.btnLight} disabled={!s.active}>Slå av nu</button>}
                            <button onClick={() => setOpenHistory(openHistory === s.id ? null : s.id)} style={ui.btnLightNeutral}>
                                {openHistory === s.id ? 'Dölj historik' : 'Historik'}
                            </button>
                            {canEdit && <button onClick={() => setEdit(s)} style={ui.btnLight}>Ändra</button>}
                            {canEdit && <button onClick={() => remove(s)} style={ui.btnLightDanger}>Ta bort</button>}
                        </div>
                    </div>
                    {openHistory === s.id && <RunHistory scheduleId={s.id} canEdit={canEdit} refreshKey={refreshKey} onError={setError} />}
                </div>
            ))}
        </div>
    );
};

export default DeviceSchedules;
